import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');
      const response = await fetch('https://readdy.ai/api/form/d9muv5cit35ii3jdlmh0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(serverMsg || '提交失败，请稍后再试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后再试');
      setFormStatus('error');
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '555电影是否需要注册？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '555电影完全免费，无需注册即可在线观看所有影视资源。',
        },
      },
      {
        '@type': 'Question',
        name: '555电影的影视资源是否高清？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '555电影所有影视资源均为高清画质，播放流畅不缓冲，为您提供最佳的观影体验。',
        },
      },
      {
        '@type': 'Question',
        name: '555电影支持哪些设备观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '555电影支持电脑、平板、手机等多种设备在线观看，兼容主流浏览器。',
        },
      },
      {
        '@type': 'Question',
        name: '555电影的影视资源更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '555电影每日更新最新热播剧集，确保您不错过任何精彩内容。国产剧、韩剧、美剧、动漫、综艺应有尽有。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系555电影客服？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过在线客服、电子邮件 contact@555movie.com 或本站的联系表单与我们取得联系，我们提供7×24小时服务。',
        },
      },
    ],
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '关于我们 - 555电影',
    description: '了解555电影品牌故事、平台口碑、联系方式及常见问题。555电影致力于提供免费高清在线影视观看体验。',
    url: 'https://www.555movie.com/about',
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '555电影',
    url: 'https://www.555movie.com',
    description: '555电影为您汇集电视剧全集免费在线观看，最新热播剧集、国产剧、韩剧等高清资源，无需缓冲，追剧首选平台。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@555movie.com',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-[1200px] mx-auto">
              <div className="mb-4">
                <Breadcrumb
                  items={[
                    { label: '首页', href: '/' },
                    { label: '关于我们' },
                  ]}
                />
              </div>

              <section className="mb-16">
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-6 text-center">
                  关于555电影
                </h1>
                <div className="max-w-3xl mx-auto">
                  <p className="text-foreground-600 leading-relaxed text-center mb-6">
                    <strong>555电影</strong>是一个专注于提供<strong>免费高清在线影视</strong>观看服务的平台。
                    我们致力于为广大追剧爱好者提供最新、最全的<strong>电视剧全集在线观看</strong>资源，
                    涵盖<strong>国产剧</strong>、<strong>韩剧</strong>、美剧、动漫、综艺等各类影视内容。
                  </p>
                  <p className="text-foreground-600 leading-relaxed text-center">
                    自成立以来，555电影始终坚持用户至上的理念，不断优化播放体验，确保<strong>高清不缓冲</strong>，
                    让每一位用户都能享受流畅的观影体验。我们相信，好的影视内容应该被更多人看到，
                    因此555电影始终提供完全免费的在线观看服务。
                  </p>
                </div>
              </section>

              <section className="mb-16">
                <div className="bg-background-900 rounded-2xl p-8 md:p-12">
                  <h2 className="text-2xl font-heading font-bold text-white mb-8 text-center">平台数据</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                    <div className="text-center">
                      <div className="text-3xl md:text-4xl font-heading font-bold text-accent-400 mb-2">5000+</div>
                      <p className="text-sm text-foreground-300">影视资源</p>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl md:text-4xl font-heading font-bold text-accent-400 mb-2">100万+</div>
                      <p className="text-sm text-foreground-300">日活跃用户</p>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl md:text-4xl font-heading font-bold text-accent-400 mb-2">99.9%</div>
                      <p className="text-sm text-foreground-300">播放成功率</p>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl md:text-4xl font-heading font-bold text-accent-400 mb-2">7×24</div>
                      <p className="text-sm text-foreground-300">在线客服</p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-16">
                <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">用户口碑</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-background-100 rounded-xl p-6">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }, (_, i) => (
                        <i key={i} className="ri-star-fill text-accent-400 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      "555电影真的是追剧神器！资源特别全，国产剧韩剧都有，而且真的不缓冲，
                      高清画质看得很舒服。推荐给所有喜欢追剧的朋友！"
                    </p>
                    <p className="text-xs text-foreground-400 font-medium">—— 追剧达人小王</p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-6">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }, (_, i) => (
                        <i key={i} className="ri-star-fill text-accent-400 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      "用了555电影之后就没换过别的平台了，更新快、画质好，而且完全免费，
                      不用注册就能看，太方便了！强烈安利！"
                    </p>
                    <p className="text-xs text-foreground-400 font-medium">—— 剧荒患者小李</p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-6">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }, (_, i) => (
                        <i key={i} className="ri-star-fill text-accent-400 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      "动漫资源也很多，国产动漫基本都能找到。画质清晰加载快，
                      用手机看也很流畅，体验非常棒！"
                    </p>
                    <p className="text-xs text-foreground-400 font-medium">—— 动漫爱好者小陈</p>
                  </div>
                </div>
              </section>

              <section className="mb-16">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-6">联系方式</h2>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                          <i className="ri-mail-line text-primary-500"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-950 mb-1">电子邮件</p>
                          <p className="text-sm text-foreground-500">contact@555movie.com</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                          <i className="ri-customer-service-2-line text-accent-500"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-950 mb-1">在线客服</p>
                          <p className="text-sm text-foreground-500">7×24小时在线，随时为您服务</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 flex-shrink-0">
                          <i className="ri-time-line text-secondary-500"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-950 mb-1">工作时间</p>
                          <p className="text-sm text-foreground-500">周一至周日 全天24小时</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-6">给我们留言</h2>
                    <form
                      data-readdy-form
                      id="contact-form"
                      onSubmit={handleSubmit}
                      className="space-y-4"
                    >
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
                        <input
                          type="text"
                          id="contact-name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">邮箱</label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">留言内容</label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                          placeholder="请输入您的留言内容（最多500字）"
                        ></textarea>
                      </div>
                      <input
                        type="text"
                        name="phone_alt"
                        className="honeypot-field"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                      <button
                        type="submit"
                        disabled={formStatus === 'loading'}
                        className="w-full px-6 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
                      >
                        {formStatus === 'loading' ? '提交中...' : '发送留言'}
                      </button>
                      {formStatus === 'success' && (
                        <p className="text-sm text-green-600">留言已成功发送，我们会尽快回复您！</p>
                      )}
                      {formStatus === 'error' && (
                        <p className="text-sm text-red-500">{formError}</p>
                      )}
                    </form>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">常见问题</h2>
                <div className="max-w-3xl mx-auto space-y-3">
                  {faqSchema.mainEntity.map((item, index) => (
                    <details key={index} className="group bg-background-100 rounded-xl overflow-hidden">
                      <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                        <span className="text-sm font-medium text-foreground-950 pr-4">{item.name}</span>
                        <i className="ri-arrow-down-s-line text-foreground-400 transition-transform group-open:rotate-180 flex-shrink-0"></i>
                      </summary>
                      <div className="px-6 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{item.acceptedAnswer.text}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}